import React, { useState, useMemo, useCallback } from "react";
import MultiSelectDropdown from "./MultiSelectDropdown";

const isTaskDone = (completed) =>
  completed === true || completed === 1 || String(completed) === "true";

const getTaskStatus = (task) => {
  if (task.waitingFor) return 'Čaká';
  const stList = task.subtasks || [];
  const doneCount = stList.filter(st => isTaskDone(st.completed)).length;
  if (stList.length > 0 && doneCount === stList.length) return 'Na odoslanie';
  if (doneCount > 0) return 'Prebieha';
  return 'Nezačaté';
};

const STATUS_STYLES = {
  'Nezačaté':     { background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' },
  'Prebieha':     { background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd' },
  'Na odoslanie': { background: '#fef3c7', color: '#b45309', border: '1px solid #fbbf24' },
  'Čaká':         { background: '#fef9c3', color: '#a16207', border: '1px solid #fde047' },
};

const ALL_STATUSES = ['Nezačaté', 'Prebieha', 'Na odoslanie', 'Čaká'];

const MONTHS_SK = [
  "Január", "Február", "Marec", "Apríl", "Máj", "Jún",
  "Júl", "August", "September", "Október", "November", "December",
];

function fmtShort(dateStr) {
  if (!dateStr) return "";
  const [, m, d] = dateStr.split("-");
  return `${parseInt(d)}.${parseInt(m)}.`;
}

export default function PovinnostiMapa({ tasks }) {
  const today = new Date();
  const [filterMonth, setFilterMonth] = useState(today.getMonth());
  const [filterYear, setFilterYear] = useState(today.getFullYear());
  const [filterCompanies, setFilterCompanies] = useState([]);
  const [filterTasks, setFilterTasks] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [tooltip, setTooltip] = useState(null);

  const activeTasks = useMemo(() =>
    (tasks || []).filter(t => !String(t.id).includes("_hist_")),
    [tasks]
  );

  const allCompanies = useMemo(() => {
    const set = new Set(activeTasks.map(t => t.company || "—"));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "sk"));
  }, [activeTasks]);

  const allTaskTexts = useMemo(() => {
    const map = new Map();
    activeTasks.forEach(t => {
      const text = (t.text || "").trim();
      if (text) map.set(text.toLowerCase(), text);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, "sk"));
  }, [activeTasks]);

  const filteredTasks = useMemo(() => {
    const monthStr = String(filterMonth + 1).padStart(2, "0");
    const prefix = `${filterYear}-${monthStr}`;
    return activeTasks.filter(t => {
      if (!t.deadline || !t.deadline.startsWith(prefix)) return false;
      if (filterCompanies.length > 0 && !filterCompanies.includes(t.company || "—")) return false;
      if (filterTasks.length > 0 && !filterTasks.some(f => f.toLowerCase() === (t.text || "").trim().toLowerCase())) return false;
      if (isTaskDone(t.completed)) return false;
      if (filterStatuses.length > 0 && !filterStatuses.includes(getTaskStatus(t))) return false;
      return true;
    });
  }, [activeTasks, filterMonth, filterYear, filterCompanies, filterTasks, filterStatuses]);

  const companies = useMemo(() => {
    const set = new Set(filteredTasks.map(t => t.company || "—"));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "sk"));
  }, [filteredTasks]);

  const deadlines = useMemo(() => {
    const set = new Set(filteredTasks.map(t => t.deadline).filter(Boolean));
    return Array.from(set).sort();
  }, [filteredTasks]);

  // Grid: company -> deadline -> tasks[]
  const grid = useMemo(() => {
    const g = {};
    companies.forEach(c => {
      g[c] = {};
      deadlines.forEach(d => { g[c][d] = []; });
    });
    filteredTasks.forEach(task => {
      const d = task.deadline || "";
      const c = task.company || "—";
      if (g[c]?.[d] !== undefined) g[c][d].push(task);
    });
    return g;
  }, [filteredTasks, companies, deadlines]);

  const goMonth = (delta) => {
    let m = filterMonth + delta;
    let y = filterYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setFilterMonth(m);
    setFilterYear(y);
  };

  const handleMouseMove = useCallback((e, cellTasks) => {
    setTooltip({ x: e.clientX, y: e.clientY, tasks: cellTasks });
  }, []);

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  return (
    <div className="mapa-root">
      <style>{`
        .mapa-root { font-family: Arial, sans-serif; overflow-x: auto; position: relative; }
        .mapa-layout { display: flex; gap: 18px; align-items: flex-start; }
        .mapa-main { flex: 1; min-width: 0; }
        .mapa-sidebar {
          min-width: 130px; flex-shrink: 0;
          background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
          padding: 12px 10px; font-size: 13px;
        }
        .mapa-sidebar-title {
          font-weight: 700; font-size: 12px; color: #64748b;
          text-transform: uppercase; letter-spacing: 0.5px;
          margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center;
        }
        .mapa-sidebar-item {
          display: flex; align-items: center; gap: 7px;
          padding: 5px 6px; border-radius: 6px; cursor: pointer;
          margin-bottom: 3px; transition: background 0.12s;
          font-size: 13px; font-weight: 500;
        }
        .mapa-sidebar-item:hover { background: #e2e8f0; }
        .mapa-sidebar-item.active { font-weight: 700; }
        .mapa-sidebar-dot {
          width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
        }
        .mapa-controls {
          display: flex; align-items: center; gap: 12px;
          margin-bottom: 14px; flex-wrap: wrap;
        }
        .mapa-month-nav { display: flex; align-items: center; gap: 4px; }
        .mapa-month-nav button {
          background: #2c3e50; color: #fff; border: none;
          border-radius: 4px; padding: 5px 11px; cursor: pointer; font-size: 15px; line-height: 1;
        }
        .mapa-month-nav button:hover { background: #3d5166; }
        .mapa-month-label {
          font-weight: bold; font-size: 14px;
          min-width: 140px; text-align: center;
        }
        .mapa-filter-select {
          padding: 5px 8px; font-size: 13px; border-radius: 4px;
          border: 1px solid #ccc; cursor: pointer; background: #fff;
        }
        .mapa-check-label {
          display: flex; align-items: center; gap: 6px; font-size: 13px; cursor: pointer;
        }
        .mapa-btn-print {
          padding: 5px 16px; font-size: 13px; cursor: pointer;
          background: #2c3e50; color: #fff; border: none;
          border-radius: 4px; margin-left: auto;
        }
        .mapa-btn-print:hover { background: #3d5166; }

        .mapa-table { border-collapse: collapse; min-width: 100%; }
        .mapa-table th {
          background: #2c3e50; color: #fff;
          padding: 8px 10px; font-size: 12px;
          text-align: center; border: 1px solid #4a5568;
          white-space: nowrap;
        }
        .mapa-table th.th-firma {
          text-align: left; min-width: 130px;
          background: #1a252f; position: sticky; left: 0; z-index: 2;
        }
        .mapa-table td {
          border: 1px solid #e0e0e0; padding: 5px 7px;
          vertical-align: top; min-width: 90px; max-width: 180px;
        }
        .mapa-table td.td-firma {
          font-weight: 600; font-size: 12px;
          background: #f0f4f8; white-space: nowrap;
          color: #2c3e50; position: sticky; left: 0; z-index: 1;
          border-right: 2px solid #c8d8ea;
        }
        .mapa-table tbody tr:hover td { background: #f9fbe7; }
        .mapa-table tbody tr:hover td.td-firma { background: #e8f0fe; }

        .task-chip {
          display: block;
          border-radius: 5px; padding: 3px 7px; margin: 2px 0;
          font-size: 11px; line-height: 1.4; cursor: default;
          word-break: break-word;
        }

        .mapa-empty {
          padding: 40px; color: #888; text-align: center; font-size: 14px;
        }

        .mapa-tooltip {
          position: fixed; background: #1e2a35; color: #f0f0f0;
          padding: 10px 14px; border-radius: 7px; font-size: 12px;
          max-width: 280px; z-index: 9999; pointer-events: none;
          box-shadow: 0 4px 16px rgba(0,0,0,0.35); line-height: 1.5;
        }
        .mapa-tt-task + .mapa-tt-task { margin-top: 8px; padding-top: 8px; border-top: 1px solid #3a4e5e; }
        .mapa-tt-name { font-weight: bold; }
        .mapa-tt-name.done { text-decoration: line-through; color: #999; }
        .mapa-tt-sub { font-size: 11px; color: #b0c0cc; margin-top: 3px; }
        .mapa-tt-note { font-size: 11px; color: #90a8b4; font-style: italic; margin-top: 2px; }

        @media print {
          .mapa-controls { display: none !important; }
          .mapa-sidebar { display: none !important; }
          .mapa-tooltip { display: none !important; }
          body * { visibility: hidden; }
          .mapa-root, .mapa-root * { visibility: visible; }
          .mapa-root { position: absolute; top: 0; left: 0; width: 100%; overflow: visible; }
          @page { size: A4 landscape; margin: 8mm 7mm; }
          .mapa-table th, .mapa-table td { font-size: 8pt; padding: 3px 5px; }
          .task-chip { font-size: 7.5pt; padding: 2px 5px; }
          .mapa-table td.td-firma, .mapa-table th.th-firma { position: static; }
        }
      `}</style>

      {/* Controls */}
      <div className="mapa-controls">
        <div className="mapa-month-nav">
          <button onClick={() => goMonth(-1)} title="Predchádzajúci mesiac">‹</button>
          <span className="mapa-month-label">{MONTHS_SK[filterMonth]} {filterYear}</span>
          <button onClick={() => goMonth(1)} title="Nasledujúci mesiac">›</button>
        </div>

        <MultiSelectDropdown
          options={allCompanies}
          selected={filterCompanies}
          onChange={setFilterCompanies}
          placeholder="Všetky firmy"
          searchPlaceholder="Hľadať firmu..."
        />

        <MultiSelectDropdown
          options={allTaskTexts}
          selected={filterTasks}
          onChange={setFilterTasks}
          placeholder="Všetky povinnosti"
          searchPlaceholder="Hľadať povinnosť..."
        />

        <button className="mapa-btn-print" onClick={() => window.print()}>Tlačiť</button>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="mapa-tooltip"
          style={{ left: tooltip.x + 16, top: tooltip.y - 10 }}
        >
          {tooltip.tasks.map(t => {
            const done = isTaskDone(t.completed);
            const st = getTaskStatus(t);
            const ss = STATUS_STYLES[st] || {};
            return (
              <div key={t.id} className="mapa-tt-task">
                <div className={`mapa-tt-name${done ? " done" : ""}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {done ? "✓ " : ""}{t.text}
                  <span style={{ fontSize: 10, fontWeight: 600, borderRadius: 4, padding: '1px 5px', ...ss }}>{st}</span>
                </div>
                {t.subtasks?.length > 0 && (
                  <div className="mapa-tt-sub">
                    {t.subtasks.map(st => (
                      <div key={st.id} style={isTaskDone(st.completed) ? { textDecoration: "line-through", color: "#666" } : {}}>
                        · {st.text}
                      </div>
                    ))}
                  </div>
                )}
                {t.waitingFor && <div className="mapa-tt-note">⏳ Čakám na: {t.waitingFor}</div>}
                {t.note && <div className="mapa-tt-note">{t.note}</div>}
              </div>
            );
          })}
        </div>
      )}

      <div className="mapa-layout">
        {/* Tabuľka */}
        <div className="mapa-main">
          {companies.length === 0 || deadlines.length === 0 ? (
            <div className="mapa-empty">
              Žiadne povinnosti pre {MONTHS_SK[filterMonth]} {filterYear}
              {filterCompanies.length > 0 ? ` — ${filterCompanies.join(", ")}` : ""}.
            </div>
          ) : (
            <table className="mapa-table">
              <thead>
                <tr>
                  <th className="th-firma">Firma</th>
                  {deadlines.map(d => (
                    <th key={d} title={d.split("-").reverse().join(".")}>{fmtShort(d)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {companies.map(c => (
                  <tr key={c}>
                    <td className="td-firma">{c}</td>
                    {deadlines.map(d => {
                      const cellTasks = grid[c][d] || [];
                      return (
                        <td
                          key={d}
                          onMouseMove={cellTasks.length > 0 ? (e) => handleMouseMove(e, cellTasks) : undefined}
                          onMouseLeave={cellTasks.length > 0 ? handleMouseLeave : undefined}
                        >
                          {cellTasks.map(task => {
                            const st = getTaskStatus(task);
                            const ss = STATUS_STYLES[st] || { background: '#e8f4fd', border: '1px solid #a8d4f5', color: '#1e3a5f' };
                            return (
                              <div key={task.id} className="task-chip" style={{ background: ss.background, border: ss.border, color: ss.color }}>
                                {task.text}
                              </div>
                            );
                          })}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Sidebar — filter stavov */}
        <div className="mapa-sidebar">
          <div className="mapa-sidebar-title">
            <span>Výber STAVU</span>
            {filterStatuses.length > 0 && (
              <button onClick={() => setFilterStatuses([])} style={{ fontSize: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }} title="Zrušiť filter">✕</button>
            )}
          </div>
          {ALL_STATUSES.map(s => {
            const ss = STATUS_STYLES[s];
            const active = filterStatuses.includes(s);
            const count = filteredTasks.filter(t => getTaskStatus(t) === s).length +
              (filterStatuses.length === 0 || active ? 0 : 0);
            // Count without status filter applied
            const countAll = activeTasks.filter(t => {
              const monthStr = String(filterMonth + 1).padStart(2, "0");
              const prefix = `${filterYear}-${monthStr}`;
              if (!t.deadline || !t.deadline.startsWith(prefix)) return false;
              if (filterCompanies.length > 0 && !filterCompanies.includes(t.company || "—")) return false;
              if (filterTasks.length > 0 && !filterTasks.some(f => f.toLowerCase() === (t.text || "").trim().toLowerCase())) return false;
              if (isTaskDone(t.completed)) return false;
              return getTaskStatus(t) === s;
            }).length;
            return (
              <div
                key={s}
                className={`mapa-sidebar-item${active ? ' active' : ''}`}
                onClick={() => setFilterStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                style={active ? { background: ss.background, color: ss.color } : {}}
              >
                <span className="mapa-sidebar-dot" style={{ background: ss.color }} />
                <span style={{ flex: 1 }}>{s}</span>
                <span style={{ fontSize: 11, color: active ? ss.color : '#94a3b8', fontWeight: 600 }}>{countAll}</span>
              </div>
            );
          })}
          {/* Legenda */}
          <div style={{ marginTop: 14, borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, fontWeight: 600 }}>Farby:</div>
            {ALL_STATUSES.map(s => {
              const ss = STATUS_STYLES[s];
              return (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3, fontSize: 11 }}>
                  <span style={{ width: 22, height: 10, borderRadius: 3, display: 'inline-block', background: ss.background, border: ss.border }} />
                  <span style={{ color: '#475569' }}>{s}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
